import { z } from "zod";

/**
 * Bump when the persisted layer shape changes in a backwards-incompatible way so
 * the client can migrate or ignore documents it does not understand.
 */
export const ANNOTATION_DOCUMENT_VERSION = 1;

/** Shared palette for text + drawing tools (kept in shared so API + mobile agree). */
export const ANNOTATION_COLORS = [
  "#FFFFFF",
  "#000000",
  "#FF3B30",
  "#FF9500",
  "#FFCC00",
  "#34C759",
  "#0A84FF",
  "#AF52DE",
  "#FF2D55",
] as const;

/** Stroke + font sizes are stored as a fraction of the canvas width (resolution independent). */
export const MIN_STROKE_WIDTH = 0.004;
export const MAX_STROKE_WIDTH = 0.08;
export const MIN_TEXT_FONT_SCALE = 0.03;
export const MAX_TEXT_FONT_SCALE = 0.4;
export const MAX_ANNOTATION_LAYERS = 200;

const HexColorSchema = z
  .string()
  .regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, "Color must be a hex value");

/** Coordinates are normalized 0..1 against intrinsic media dimensions; finite by contract. */
const NormalizedCoordSchema = z.number().finite();
const NormalizedPointSchema = z.tuple([
  NormalizedCoordSchema,
  NormalizedCoordSchema,
]);

const BaseLayerSchema = z.object({
  id: z.string().min(1),
});

export const DrawingLayerSchema = BaseLayerSchema.extend({
  type: z.literal("drawing"),
  color: HexColorSchema,
  /** Fraction of canvas width. */
  strokeWidth: z.number().positive(),
  points: z.array(NormalizedPointSchema).min(1),
});
export type DrawingLayer = z.infer<typeof DrawingLayerSchema>;

export const TextLayerSchema = BaseLayerSchema.extend({
  type: z.literal("text"),
  text: z.string().min(1).max(500),
  color: HexColorSchema,
  /** Normalized center of the text box. */
  x: NormalizedCoordSchema,
  y: NormalizedCoordSchema,
  /** Font size as a fraction of canvas width. */
  fontScale: z.number().positive(),
  /** Rotation in radians. */
  rotation: z.number().finite(),
});
export type TextLayer = z.infer<typeof TextLayerSchema>;

export const AnnotationLayerSchema = z.discriminatedUnion("type", [
  DrawingLayerSchema,
  TextLayerSchema,
]);
export type AnnotationLayer = z.infer<typeof AnnotationLayerSchema>;

export const AnnotationDocumentSchema = z.object({
  version: z
    .literal(ANNOTATION_DOCUMENT_VERSION)
    .default(ANNOTATION_DOCUMENT_VERSION),
  /** Order is z-order: earlier layers render first (underneath). */
  layers: z.array(AnnotationLayerSchema).max(MAX_ANNOTATION_LAYERS),
});
export type AnnotationDocument = z.infer<typeof AnnotationDocumentSchema>;

export const EMPTY_ANNOTATION_DOCUMENT: AnnotationDocument = {
  version: ANNOTATION_DOCUMENT_VERSION,
  layers: [],
};

export const UpdatePhotoAnnotationsSchema = z.object({
  annotations: AnnotationDocumentSchema,
  /** Flattened JPEG render key; null clears any existing render. */
  annotatedStorageKey: z.string().nullable().optional(),
});
export type UpdatePhotoAnnotationsInput = z.infer<
  typeof UpdatePhotoAnnotationsSchema
>;

export const AnnotationUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  annotatedStorageKey: z.string(),
});
export type AnnotationUploadUrlResponse = z.infer<
  typeof AnnotationUploadUrlResponseSchema
>;

export const PhotoAnnotationsResponseSchema = z.object({
  annotations: AnnotationDocumentSchema.nullable(),
});
export type PhotoAnnotationsResponse = z.infer<
  typeof PhotoAnnotationsResponseSchema
>;
